import type { ExtendedDataPolygonBSP, LineBSP, LineSplitBSP, NodeBSP, PlaneBSP, PolygonBSP } from "./bspTypes";
import { Pool } from "./pool";

const INITIAL_OBJECT_COUNT = 8192;

export class AllocatorsBSP {
  private planePool: Pool<PlaneBSP>;
  private polygonPool: Pool<PolygonBSP>;
  private nodePool: Pool<NodeBSP>;
  private extendedDataPolygonPool: Pool<ExtendedDataPolygonBSP>;
  private lineSplitPool: Pool<LineSplitBSP>;
  private linePool: Pool<LineBSP>;

  constructor(
    readonly initialPlaneCount: number = INITIAL_OBJECT_COUNT,
    readonly initialPolygonCount: number = INITIAL_OBJECT_COUNT,
    readonly initialNodeCount: number = INITIAL_OBJECT_COUNT,
    readonly initialExtendedDataPolygonCount: number = INITIAL_OBJECT_COUNT,
    readonly initialLineSplitCount: number = INITIAL_OBJECT_COUNT,
    readonly initialLineCount: number = INITIAL_OBJECT_COUNT,
  ) {
    this.planePool = new Pool<PlaneBSP>((_i) => {
      return { nX: 0, nY: 0, nZ: 0, dist: 0 };
    }, initialPlaneCount);

    this.polygonPool = new Pool<PolygonBSP>((i) => {
      return { priority: i, vertices: [], plane: { nX: 0, nY: 0, nZ: 0, dist: 0 }, extendedData: undefined };
    }, initialPolygonCount);

    this.nodePool = new Pool<NodeBSP>((_i) => {
      return {
        plane: { nX: 0, nY: 0, nZ: 0, dist: 0 },
        polygons: [],
        parentNode: null,
        frontNode: null,
        backNode: null,
        userData: 0,
      };
    }, initialNodeCount);

    this.extendedDataPolygonPool = new Pool<ExtendedDataPolygonBSP>((i) => {
      return {
        id: i,
        aabb: { minX: 0, minY: 0, minZ: 0, maxX: 0, maxY: 0, maxZ: 0 },
        visibleLines: [],
        splitLines: [],
        hatchLines: [],
        neighborNormals: [],
        edgePlanes: [],
        hatchProperties: undefined,
        entityId: undefined,
      };
    }, initialExtendedDataPolygonCount);

    this.lineSplitPool = new Pool<LineSplitBSP>((_i) => {
      return {
        frontPolygon: {
          priority: 0,
          vertices: [],
          plane: { nX: 0, nY: 0, nZ: 0, dist: 0 },
        },
        line: { x1: 0, y1: 0, z1: 0, x2: 0, y2: 0, z2: 0 },
      };
    }, initialLineSplitCount);

    this.linePool = new Pool<LineBSP>((_i) => {
      return { x1: 0, y1: 0, z1: 0, x2: 0, y2: 0, z2: 0 };
    }, initialLineCount);
  }

  allocPlane(): PlaneBSP {
    return this.planePool.alloc();
  }

  allocPolygon(): PolygonBSP {
    return this.polygonPool.alloc();
  }

  allocNode(): NodeBSP {
    return this.nodePool.alloc();
  }

  allocExtendedDataPolygon(): ExtendedDataPolygonBSP {
    return this.extendedDataPolygonPool.alloc();
  }

  allocLineSplit(): LineSplitBSP {
    return this.lineSplitPool.alloc();
  }

  allocLine(): LineBSP {
    return this.linePool.alloc();
  }

  reset() {
    this.planePool.reset();
    this.polygonPool.reset();
    this.nodePool.reset();
    this.extendedDataPolygonPool.reset();
    this.lineSplitPool.reset();
    this.linePool.reset();
  }
}

let allocatorsBSP: AllocatorsBSP | undefined = undefined;

export function getBSPAllocators(): AllocatorsBSP | undefined {
  return allocatorsBSP;
}

export function setBSPAllocators(newAlloc: AllocatorsBSP | undefined) {
  allocatorsBSP = newAlloc;
}
